import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { layersApi, type LayerChange, type LayerChangeAction } from '../../lib/layersApi';

const ACTION_LABELS: Record<LayerChangeAction, string> = {
  feature_created: 'Объект создан',
  feature_updated: 'Объект изменён',
  feature_deleted: 'Объект удалён',
  layer_updated: 'Изменены настройки слоя',
  layer_style_imported: 'Импортирован стиль',
  layer_published: 'Слой опубликован',
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}

export function LayerHistoryModal({
  layerId,
  layerName,
  onClose,
  onRolledBack,
}: {
  layerId: string;
  layerName: string;
  onClose: () => void;
  onRolledBack?: () => void;
}) {
  const [events, setEvents] = useState<LayerChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<LayerChange | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    layersApi
      .listLayerHistory(layerId)
      .then(setEvents)
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить историю изменений.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [layerId]);

  const handleRollback = async () => {
    if (!confirmTarget) return;
    setRollingBack(true);
    setRollbackError(null);
    try {
      await layersApi.rollbackLayer(layerId, confirmTarget.id);
      setConfirmTarget(null);
      load();
      onRolledBack?.();
    } catch (err) {
      setRollbackError(err instanceof ApiError ? err.message : 'Не удалось откатить изменения.');
    } finally {
      setRollingBack(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/52 p-4">
      <div className="max-h-[85vh] w-full max-w-[640px] overflow-y-auto rounded-[18px] border border-border-default bg-bg-surface p-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-text-primary">История изменений — «{layerName}»</h2>
          <button type="button" onClick={onClose} aria-label="Закрыть" className="shrink-0 text-lg font-semibold text-text-secondary hover:text-text-primary">
            ×
          </button>
        </div>
        <p className="mt-1 text-xs text-text-secondary">Хранится 7 дней, дальше события удаляются автоматически.</p>

        {loading ? (
          <p className="mt-4 text-xs text-text-secondary">Загружаем…</p>
        ) : loadError ? (
          <p className="mt-4 text-xs font-medium text-status-danger">{loadError}</p>
        ) : events.length === 0 ? (
          <p className="mt-4 text-xs text-text-secondary">За последние 7 дней изменений не было.</p>
        ) : (
          <div className="mt-4 flex flex-col divide-y divide-border-default">
            {events.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-text-primary">{ACTION_LABELS[e.action] ?? e.action}</p>
                  <p className="mt-0.5 truncate text-[10px] text-text-secondary">
                    {e.actor_name} · {formatWhen(e.occurred_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmTarget(e)}
                  className="shrink-0 text-[11px] font-semibold text-action-primary hover:underline"
                >
                  Откатить к этому моменту
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/52 p-4">
          <div className="w-full max-w-[440px] rounded-[18px] border border-border-default bg-bg-surface p-6">
            <h3 className="text-base font-semibold text-text-primary">Откатить изменения?</h3>
            <p className="mt-2 text-sm text-text-secondary">
              Слой «{layerName}» вернётся к состоянию на момент «{ACTION_LABELS[confirmTarget.action] ?? confirmTarget.action}»
              ({formatWhen(confirmTarget.occurred_at)}). Более поздние изменения объектов будут отменены — это тоже
              запишется в историю, откат можно откатить.
            </p>
            {rollbackError && <p className="mt-3 text-xs font-medium text-status-danger">{rollbackError}</p>}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmTarget(null)}
                disabled={rollingBack}
                className="flex h-10 items-center rounded-md border border-border-strong bg-bg-surface px-4 text-sm font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleRollback}
                disabled={rollingBack}
                className="flex h-10 items-center rounded-md bg-status-danger px-4 text-sm font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {rollingBack ? 'Откатываем…' : 'Да, откатить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
